import React, {useState} from 'react';
import {Organization} from '../types';
import {SubscriptionStatus, Invoice, DowngradePreviewResponse, CheckoutSession} from '../services/apiService';
import {PaddleService} from '../services/paddleService';
import {
    CreditCard,
    Check,
    AlertCircle,
    X,
    CheckCircle2,
    Sparkles,
    Calendar,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Download,
    FileText,
    Clock,
    Receipt,
    Shield,
    Zap,
    ArrowRight,
    Settings,
} from 'lucide-react';
import {Button} from './ui/Button';
import {Card} from './ui/Card';
import {Tag} from './ui/Tag';

interface BillingProps {
    organization: Organization;
    subscriptionStatus: SubscriptionStatus | null;
    invoices: Invoice[];
    /** True while the subscription status is being fetched for the first time. */
    subscriptionLoading: boolean;
    /** True while the invoice list is being fetched for the first time. */
    invoicesLoading: boolean;
    /** Fetch the Paddle.js config needed to open overlay checkout for Pro. */
    onStartCheckout: () => Promise<CheckoutSession>;
    /** Called after Paddle reports checkout completed; syncs server state and
     *  returns true once Pro is confirmed, false if the webhook hasn't landed. */
    onCheckoutComplete: () => Promise<boolean>;
    /** Cancel at period end; the org keeps Pro until then. */
    onCancelSubscription: (reason?: string) => Promise<void>;
    /** Remove a scheduled cancellation so the subscription keeps running. */
    onResumeSubscription: () => Promise<void>;
    /** Open the Paddle customer portal; returns a URL to navigate to. */
    onOpenPortal: () => Promise<string>;
    onRefreshSubscription: () => Promise<void>;
    onGetDowngradePreview: () => Promise<DowngradePreviewResponse>;
    userId: string;
    userEmail: string;
}

const PLANS = {
    free: {
        name: 'Free',
        price: 0,
        period: '',
        description: 'For small teams getting started',
        features: [
            {text: '1 active rule', included: true},
            {text: 'Unlimited repos', included: true},
            {text: 'Unlimited PRs', included: true},
            {text: 'All features included', included: true},
            {text: 'Unlimited rules', included: false},
            {text: 'Priority support', included: false},
        ],
    },
    pro: {
        name: 'Pro',
        price: 25,
        period: '/mo',
        description: 'For teams that need more',
        features: [
            {text: 'Unlimited active rules', included: true},
            {text: 'Unlimited repos', included: true},
            {text: 'Unlimited PRs', included: true},
            {text: 'All features included', included: true},
            {text: 'Priority support', included: true},
        ],
        trial: '1 month free trial',
    },
};

const DOWNGRADE_REASONS = [
    {id: 'too_expensive', label: 'Too expensive'},
    {id: 'not_using_enough', label: 'Not using it enough'},
    {id: 'missing_features', label: 'Missing features I need'},
    {id: 'switching_tools', label: 'Switching to another tool'},
    {id: 'temporary', label: 'Just need to pause for now'},
    {id: 'other', label: 'Other reason'},
];

const FAQ_ITEMS_FREE = [
    {
        question: 'What happens when I upgrade?',
        answer: 'Your workspace will immediately unlock unlimited rules. You can create and enable as many rules as you need. The 1-month free trial starts right away, and you won\'t be charged until the trial ends.'
    },
    {
        question: 'Can I cancel anytime?',
        answer: 'Yes! You can cancel at any time. Your subscription stays active until the end of your current billing period, then your workspace moves to the Free plan — you won\'t be charged again. During the 1-month free trial you can cancel without ever being charged.'
    },
    {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards (Visa, Mastercard, American Express) and PayPal. All payments are securely processed through Paddle.'
    },
    {
        question: 'Is there a limit on team members?',
        answer: 'No! Unlike other tools, ReviewMe pricing is per workspace, not per seat. Invite your entire team without worrying about additional costs.'
    }
];

const FAQ_ITEMS_PRO = [
    {
        question: 'How do I update my payment method?',
        answer: 'Click "Manage Payment Method" above to access the Paddle customer portal where you can update your card details or switch payment methods.'
    },
    {
        question: 'When will I be charged?',
        answer: 'You\'ll be charged on the same day each month as when you first subscribed. If you\'re on a trial, you\'ll be charged when the trial ends.'
    },
    {
        question: 'Can I get a refund?',
        answer: 'When you cancel, your Pro features stay active until the end of the billing period and you\'re not charged again. If you cancel within 14 days of your first payment and would like a full refund, just contact us at contact@reviewme.dev and we\'ll sort it out.'
    },
    {
        question: 'What happens to my rules if I cancel?',
        answer: 'When your workspace moves to Free, only your oldest active rule will remain enabled. All other rules are automatically disabled but not deleted—you can re-enable them anytime by upgrading again.'
    }
];

// Loading shimmer that matches the app's skeleton language (see RuleCardSkeleton):
// a soft pulsing block in the same parchment tone as the card borders.
const Shimmer: React.FC<{className?: string}> = ({className = ''}) => (
    <span className={`block bg-[#e4e0d2] animate-pulse ${className}`} aria-hidden="true"/>
);

export const Billing: React.FC<BillingProps> = ({
    organization,
    subscriptionStatus,
    invoices,
    subscriptionLoading,
    invoicesLoading,
    onStartCheckout,
    onCheckoutComplete,
    onCancelSubscription,
    onResumeSubscription,
    onOpenPortal,
    onRefreshSubscription,
    onGetDowngradePreview,
    userId,
    userEmail,
}) => {
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [downgradeLoading, setDowngradeLoading] = useState(false);
    const [resumeLoading, setResumeLoading] = useState(false);
    const [billingError, setBillingError] = useState<string | null>(null);
    const [billingSuccess, setBillingSuccess] = useState<string | null>(null);
    const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

    // Change Plan Modal State
    const [showChangePlanModal, setShowChangePlanModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<'free' | 'pro' | null>(null);
    const [changePlanStep, setChangePlanStep] = useState<'select' | 'confirm'>('select');
    const [downgradePreview, setDowngradePreview] = useState<DowngradePreviewResponse | null>(null);
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
    const [otherReason, setOtherReason] = useState('');

    const isPro = organization.plan === 'pro';
    const isTrialing = subscriptionStatus?.status === 'trialing';
    // Pro, but a cancellation is already scheduled for the end of the period/trial.
    const isCanceling = isPro && !!subscriptionStatus?.cancel_at_period_end;
    const currentPlan = isPro ? 'pro' : 'free';

    // Only shimmer while data is genuinely still in-flight (no data yet). If a
    // fetch settles with an error, fall through to the normal empty/"N/A" render
    // rather than shimmering forever.
    const detailsLoading = subscriptionLoading && !subscriptionStatus;
    const invoicesSkeleton = invoicesLoading && invoices.length === 0;

    const openChangePlanModal = (preselect?: 'free' | 'pro') => {
        setSelectedPlan(preselect || null);
        setChangePlanStep('select');
        setDowngradePreview(null);
        setSelectedReasons([]);
        setOtherReason('');
        setShowChangePlanModal(true);
    };

    const closeChangePlanModal = () => {
        setShowChangePlanModal(false);
        setSelectedPlan(null);
        setChangePlanStep('select');
        setDowngradePreview(null);
        setSelectedReasons([]);
        setOtherReason('');
    };

    const handlePlanSelect = async (plan: 'free' | 'pro') => {
        if (plan === currentPlan) return;

        setSelectedPlan(plan);

        if (plan === 'free' && isPro) {
            // Fetch downgrade preview
            try {
                const preview = await onGetDowngradePreview();
                setDowngradePreview(preview);
            } catch (err) {
                console.error('Failed to fetch downgrade preview:', err);
            }
        }

        setChangePlanStep('confirm');
    };

    const handleConfirmUpgrade = async () => {
        setCheckoutLoading(true);
        setBillingError(null);

        try {
            // Fetch the per-org Paddle config, then open the real overlay checkout.
            // Pro is granted server-side via webhook; on completion we poll for it.
            const config = await onStartCheckout();

            if (!PaddleService.isAvailable()) {
                throw new Error('Checkout is temporarily unavailable. Please try again.');
            }

            PaddleService.initialize({
                clientToken: config.client_token,
                environment: config.sandbox ? 'sandbox' : 'production',
            });

            PaddleService.openCheckout({
                priceId: config.price_id,
                customerEmail: userEmail,
                customData: {
                    organizationId: organization.id,
                    userId,
                    orgToken: config.org_token,
                },
                onComplete: async () => {
                    try {
                        const confirmed = await onCheckoutComplete();
                        closeChangePlanModal();
                        if (confirmed) {
                            setBillingSuccess('Welcome to Pro! Your 1-month free trial has started.');
                            setTimeout(() => setBillingSuccess(null), 6000);
                        } else {
                            // Payment went through but the activation webhook hasn't
                            // landed yet — don't claim Pro is active.
                            setBillingSuccess('Payment received — activating your subscription. This usually takes a few seconds and the page will update automatically.');
                            setTimeout(() => setBillingSuccess(null), 9000);
                        }
                    } catch (err) {
                        setBillingError('Payment received — finishing setup. This can take a moment; refresh if your plan doesn\'t update.');
                    }
                    setCheckoutLoading(false);
                },
                onClose: () => {
                    setCheckoutLoading(false);
                },
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to start checkout';
            setBillingError(message);
            setCheckoutLoading(false);
        }
    };

    const handleConfirmCancel = async () => {
        setDowngradeLoading(true);
        setBillingError(null);

        const reasons = [...selectedReasons];
        if (selectedReasons.includes('other') && otherReason.trim()) {
            reasons.push(`Other: ${otherReason.trim()}`);
        }
        const reasonText = reasons.filter(r => r !== 'other').join('; ');

        try {
            await onCancelSubscription(reasonText || undefined);
            await onRefreshSubscription();
            closeChangePlanModal();
            const periodEnd = formatDate(
                isTrialing ? subscriptionStatus?.trial_ends_at : subscriptionStatus?.current_period_end
            );
            setBillingSuccess(
                `Your subscription is set to cancel. You'll keep Pro until ${periodEnd}, then move to the Free plan. No further charges.`
            );
            setTimeout(() => setBillingSuccess(null), 8000);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to cancel subscription';
            setBillingError(message);
        } finally {
            setDowngradeLoading(false);
        }
    };

    const handleResume = async () => {
        setResumeLoading(true);
        setBillingError(null);
        try {
            await onResumeSubscription();
            await onRefreshSubscription();
            setBillingSuccess(
                isTrialing
                    ? "Your subscription is back on. Your free trial continues and you'll move to Pro when it ends."
                    : "Your subscription is back on. It will renew as normal — no further action needed."
            );
            setTimeout(() => setBillingSuccess(null), 8000);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to resume subscription';
            setBillingError(message);
        } finally {
            setResumeLoading(false);
        }
    };

    const toggleReason = (reasonId: string) => {
        setSelectedReasons(prev =>
            prev.includes(reasonId)
                ? prev.filter(r => r !== reasonId)
                : [...prev, reasonId]
        );
    };

    const handleManageSubscription = async () => {
        setBillingError(null);
        try {
            const url = await onOpenPortal();
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to open billing portal';
            setBillingError(message);
        }
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(amount / 100);
    };

    const getInvoiceStatusBadge = (status: Invoice['status']) => {
        switch (status) {
            case 'paid':
                return <Tag tone="green">Paid</Tag>;
            case 'pending':
                return <Tag tone="amber">Pending</Tag>;
            case 'overdue':
                return <Tag tone="coral">Overdue</Tag>;
            case 'refunded':
                return <Tag tone="slate">Refunded</Tag>;
        }
    };

    const faqItems = isPro ? FAQ_ITEMS_PRO : FAQ_ITEMS_FREE;

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            {/* Error/Success Messages */}
            {billingError && (
                <div className="p-4 bg-[#fee2e2] border border-[#fecaca] rounded-xl flex items-center gap-3 text-[#b91c1c]">
                    <AlertCircle size={20}/>
                    <span className="font-medium flex-1">{billingError}</span>
                    <button onClick={() => setBillingError(null)} className="p-1 hover:bg-[#fecaca] rounded-full">
                        <X size={18}/>
                    </button>
                </div>
            )}
            {billingSuccess && (
                <div className="p-4 bg-[#dcfce7] border border-[#bbf7d0] rounded-xl flex items-center gap-3 text-[#15803d]">
                    <CheckCircle2 size={20}/>
                    <span className="font-medium flex-1">{billingSuccess}</span>
                    <button onClick={() => setBillingSuccess(null)} className="p-1 hover:bg-[#bbf7d0] rounded-full">
                        <X size={18}/>
                    </button>
                </div>
            )}

            {/* Current Plan Card */}
            <Card raised className="overflow-hidden">
                {/* Header */}
                <div className={`px-6 py-4 border-b border-[#e4e0d2] ${
                    isPro
                        ? 'bg-[#dcfce7]'
                        : 'bg-[#f3e8ff]'
                }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-xl ${isPro ? 'bg-[#22c55e]/15' : 'bg-[#a855f7]/15'}`}>
                                <Shield size={24} className={isPro ? 'text-[#15803d]' : 'text-[#7e22ce]'}/>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-[#0a0a0a]">{isPro ? 'Pro' : 'Free'} Plan</h2>
                                <p className="text-[#6f6b60] text-sm">
                                    {isPro ? 'Unlimited rules for your workspace' : '1 active rule included'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {detailsLoading ? (
                                <Shimmer className="h-6 w-20 rounded-full"/>
                            ) : (
                                <Tag tone={!isPro ? 'slate' : isCanceling ? 'amber' : isTrialing ? 'sky' : 'green'}>
                                    {!isPro ? 'Active' : isCanceling ? 'Canceling' : isTrialing ? 'Trial' : 'Active'}
                                </Tag>
                            )}
                        </div>
                    </div>
                </div>

                {/* Plan Details */}
                <div className="p-6">
                    {isPro ? (
                        <>
                            {/* Pro Plan Details */}
                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div>
                                    <div className="flex items-center gap-2 text-[#6f6b60] mb-1">
                                        <Calendar size={14}/>
                                        <span className="text-xs font-bold uppercase tracking-wide">
                                            {isTrialing ? 'Trial Started' : 'Member Since'}
                                        </span>
                                    </div>
                                    {detailsLoading ? (
                                        <Shimmer className="h-7 w-32 mt-0.5 rounded-md"/>
                                    ) : (
                                        <p className="text-lg font-bold text-[#0a0a0a]">
                                            {formatDate(subscriptionStatus?.created_at)}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 text-[#6f6b60] mb-1">
                                        <Clock size={14}/>
                                        <span className="text-xs font-bold uppercase tracking-wide">
                                            {isCanceling ? 'Cancels On' : isTrialing ? 'Trial Ends' : 'Next Payment'}
                                        </span>
                                    </div>
                                    {detailsLoading ? (
                                        <Shimmer className="h-7 w-32 mt-0.5 rounded-md"/>
                                    ) : (
                                        <p className="text-lg font-bold text-[#0a0a0a]">
                                            {formatDate(isTrialing ? subscriptionStatus?.trial_ends_at : subscriptionStatus?.current_period_end)}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 text-[#6f6b60] mb-1">
                                        <CreditCard size={14}/>
                                        <span className="text-xs font-bold uppercase tracking-wide">
                                            {isCanceling ? 'After Cancellation' : isTrialing ? 'After Trial' : 'Monthly Cost'}
                                        </span>
                                    </div>
                                    {/* A scheduled cancellation means no further charge — show $0 so we
                                        don't imply they'll be billed $25 after the trial/period ends. */}
                                    <p className="text-lg font-bold text-[#0a0a0a]">{isCanceling ? '$0.00' : '$25.00'}</p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 text-[#6f6b60] mb-1">
                                        <Receipt size={14}/>
                                        <span className="text-xs font-bold uppercase tracking-wide">Billing Cycle</span>
                                    </div>
                                    <p className="text-lg font-bold text-[#0a0a0a]">Monthly</p>
                                </div>
                            </div>

                            {isCanceling && (
                                <div className="mt-6 p-4 bg-[#fef3c7] border border-[#fde68a] rounded-xl flex flex-col sm:flex-row sm:items-center gap-4">
                                    <p className="text-[#92400e] text-sm flex-1">
                                        <span className="font-bold">Your subscription is set to cancel.</span> You'll keep Pro until {formatDate(isTrialing ? subscriptionStatus?.trial_ends_at : subscriptionStatus?.current_period_end)}, then your workspace moves to the Free plan. You won't be charged again.
                                        {isTrialing && ' Changed your mind? Keep your subscription to stay on Pro.'}
                                    </p>
                                    <Button
                                        variant="primary"
                                        onClick={handleResume}
                                        disabled={resumeLoading}
                                        className="px-5 py-2.5 text-sm whitespace-nowrap shrink-0"
                                    >
                                        {resumeLoading ? 'Resuming...' : (
                                            <>
                                                <Check size={16}/>
                                                Keep My Subscription
                                            </>
                                        )}
                                    </Button>
                                </div>
                            )}

                            {isTrialing && !isCanceling && (
                                <div className="mt-6 p-4 bg-[#e0f2fe] border border-[#bae6fd] rounded-xl">
                                    <p className="text-[#075985] text-sm">
                                        <span className="font-bold">You're on a free trial.</span> Your payment method will be charged $25.00 on {formatDate(subscriptionStatus?.trial_ends_at)}.
                                        You can change your plan anytime before then.
                                    </p>
                                </div>
                            )}

                            <div className="mt-6 pt-6 border-t border-[#ece8db] flex flex-wrap gap-3">
                                <Button
                                    variant="primary"
                                    onClick={handleManageSubscription}
                                    className="px-5 py-2.5 text-sm"
                                >
                                    <ExternalLink size={16}/>
                                    Manage Payment Method
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() => openChangePlanModal()}
                                    className="px-5 py-2.5 text-sm"
                                >
                                    <Settings size={16}/>
                                    Change Plan
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Free Plan Details */}
                            <div className="grid md:grid-cols-3 gap-6">
                                <div>
                                    <div className="flex items-center gap-2 text-[#6f6b60] mb-1">
                                        <Zap size={14}/>
                                        <span className="text-xs font-bold uppercase tracking-wide">Active Rules</span>
                                    </div>
                                    <p className="text-lg font-bold text-[#0a0a0a]">1 allowed</p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 text-[#6f6b60] mb-1">
                                        <CreditCard size={14}/>
                                        <span className="text-xs font-bold uppercase tracking-wide">Monthly Cost</span>
                                    </div>
                                    <p className="text-lg font-bold text-[#0a0a0a]">$0.00</p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 text-[#6f6b60] mb-1">
                                        <Receipt size={14}/>
                                        <span className="text-xs font-bold uppercase tracking-wide">Billing</span>
                                    </div>
                                    <p className="text-lg font-bold text-[#0a0a0a]">No billing</p>
                                </div>
                            </div>

                            <div className="mt-6 pt-6 border-t border-[#ece8db]">
                                <Button
                                    variant="primary"
                                    onClick={() => openChangePlanModal('pro')}
                                    className="px-5 py-2.5 text-sm"
                                >
                                    <Sparkles size={16}/>
                                    Upgrade to Pro
                                    <ArrowRight size={16}/>
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Card>

            {/* Billing History Section */}
            <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                    <div className="bg-[#ece8db] p-2 rounded-xl">
                        <FileText size={20} className="text-[#6f6b60]"/>
                    </div>
                    <h3 className="text-lg font-bold text-[#0a0a0a]">Billing History</h3>
                </div>

                {invoicesSkeleton ? (
                    <div className="overflow-hidden border border-[#e4e0d2] rounded-xl">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-[#f4f1e6] border-b border-[#e4e0d2]">
                                <tr>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide">Invoice</th>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide">Date</th>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide">Amount</th>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide">Status</th>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide text-right">Download</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#ece8db]">
                                {[0, 1].map((i) => (
                                    <tr key={i}>
                                        <td className="px-4 py-3">
                                            <Shimmer className="h-4 w-24 rounded-md"/>
                                            <Shimmer className="h-3 w-32 rounded-md mt-1.5"/>
                                        </td>
                                        <td className="px-4 py-3"><Shimmer className="h-4 w-28 rounded-md"/></td>
                                        <td className="px-4 py-3"><Shimmer className="h-4 w-16 rounded-md"/></td>
                                        <td className="px-4 py-3"><Shimmer className="h-6 w-16 rounded-full"/></td>
                                        <td className="px-4 py-3"><Shimmer className="h-4 w-10 rounded-md ml-auto"/></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : invoices.length > 0 ? (
                    <div className="overflow-hidden border border-[#e4e0d2] rounded-xl">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-[#f4f1e6] border-b border-[#e4e0d2]">
                                <tr>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide">Invoice</th>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide">Date</th>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide">Amount</th>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide">Status</th>
                                    <th className="px-4 py-3 text-xs font-bold text-[#6f6b60] uppercase tracking-wide text-right">Download</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#ece8db]">
                                {invoices.map((invoice) => (
                                    <tr key={invoice.id} className="hover:bg-[#f4f1e6]/60 transition-colors">
                                        <td className="px-4 py-3">
                                            <span className="text-sm font-medium text-[#0a0a0a]">{invoice.invoice_number}</span>
                                            <p className="text-xs text-[#6f6b60]">{invoice.description}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-sm text-[#6f6b60]">{formatDate(invoice.issued_at)}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-sm font-medium text-[#0a0a0a]">
                                                {formatCurrency(invoice.amount, invoice.currency)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {getInvoiceStatusBadge(invoice.status)}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {invoice.pdf_url ? (
                                                <a
                                                    href={invoice.pdf_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-[#15803d] hover:text-[#166534] font-medium text-sm"
                                                >
                                                    <Download size={14}/>
                                                    PDF
                                                </a>
                                            ) : (
                                                <span className="text-[#a8a394] text-sm">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="border border-dashed border-[#e4e0d2] rounded-xl p-8 text-center">
                        <div className="bg-[#ece8db] w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Receipt size={24} className="text-[#a8a394]"/>
                        </div>
                        <p className="text-[#6f6b60] font-medium">No invoices yet</p>
                        <p className="text-[#a8a394] text-sm mt-1">
                            {isPro
                                ? isTrialing
                                    ? 'Your first invoice will be generated when your trial ends.'
                                    : 'Invoices will appear here after your first payment.'
                                : 'Invoices will appear here when you subscribe to Pro.'}
                        </p>
                    </div>
                )}
            </Card>

            {/* FAQ Section */}
            <Card className="p-6">
                <h3 className="text-lg font-bold text-[#0a0a0a] mb-6">Frequently Asked Questions</h3>

                <div className="space-y-3">
                    {faqItems.map((item, index) => (
                        <div key={index} className="border border-[#e4e0d2] rounded-xl overflow-hidden">
                            <button
                                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#f4f1e6] transition-colors"
                            >
                                <span className="font-bold text-[#0a0a0a] text-sm">{item.question}</span>
                                {expandedFaq === index ? (
                                    <ChevronUp size={18} className="text-[#a8a394]"/>
                                ) : (
                                    <ChevronDown size={18} className="text-[#a8a394]"/>
                                )}
                            </button>
                            {expandedFaq === index && (
                                <div className="px-4 pb-4">
                                    <p className="text-[#6f6b60] text-sm leading-relaxed">{item.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Card>

            {/* Change Plan Modal */}
            {showChangePlanModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full border-2 border-[#0a0a0a] nb-shadow-lg max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-[#e4e0d2] flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
                            <h3 className="text-lg font-bold text-[#0a0a0a]">
                                {changePlanStep === 'select' ? 'Change Plan' : selectedPlan === 'pro' ? 'Upgrade to Pro' : 'Cancel Subscription'}
                            </h3>
                            <button
                                onClick={closeChangePlanModal}
                                className="p-2 hover:bg-[#ece8db] rounded-xl transition-colors"
                            >
                                <X size={20} className="text-[#6f6b60]"/>
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6">
                            {changePlanStep === 'select' ? (
                                <>
                                    <p className="text-[#6f6b60] mb-6">Select a plan for your workspace. Upgrades start right away with a 1-month free trial; cancellations take effect at the end of your billing period.</p>

                                    {/* Plan Selection */}
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {/* Free Plan */}
                                        <button
                                            onClick={() => handlePlanSelect('free')}
                                            disabled={currentPlan === 'free'}
                                            className={`text-left p-5 rounded-2xl border transition-all ${
                                                currentPlan === 'free'
                                                    ? 'border-[#e4e0d2] bg-[#f4f1e6]'
                                                    : 'border-[#e4e0d2] hover:border-[#d3cdba] hover:bg-[#f4f1e6]'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-lg font-bold text-[#0a0a0a]">{PLANS.free.name}</h4>
                                                {currentPlan === 'free' && (
                                                    <Tag tone="slate">Current</Tag>
                                                )}
                                            </div>
                                            <div className="flex items-baseline gap-1 mb-2">
                                                <span className="text-2xl font-extrabold text-[#0a0a0a]">${PLANS.free.price}</span>
                                                <span className="text-[#6f6b60] text-sm">/mo</span>
                                            </div>
                                            <p className="text-[#6f6b60] text-sm mb-4">{PLANS.free.description}</p>
                                            <ul className="space-y-2">
                                                {PLANS.free.features.slice(0, 4).map((feature, idx) => (
                                                    <li key={idx} className="flex items-center gap-2 text-sm">
                                                        {feature.included ? (
                                                            <Check size={16} className="text-[#16a34a]"/>
                                                        ) : (
                                                            <X size={16} className="text-[#cfc9b6]"/>
                                                        )}
                                                        <span className={feature.included ? 'text-[#3f3b32]' : 'text-[#a8a394]'}>{feature.text}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </button>

                                        {/* Pro Plan */}
                                        <button
                                            onClick={() => handlePlanSelect('pro')}
                                            disabled={currentPlan === 'pro'}
                                            className={`text-left p-5 rounded-2xl border-2 transition-all ${
                                                currentPlan === 'pro'
                                                    ? 'border-[#22c55e] bg-[#dcfce7]'
                                                    : 'border-[#22c55e] hover:bg-[#dcfce7]'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="text-lg font-bold text-[#0a0a0a]">{PLANS.pro.name}</h4>
                                                    {currentPlan !== 'pro' && (
                                                        <Tag tone="green">1 month free</Tag>
                                                    )}
                                                </div>
                                                {currentPlan === 'pro' && (
                                                    <Tag tone="green">Current</Tag>
                                                )}
                                            </div>
                                            <div className="flex items-baseline gap-1 mb-2">
                                                <span className="text-2xl font-extrabold text-[#0a0a0a]">${PLANS.pro.price}</span>
                                                <span className="text-[#6f6b60] text-sm">/mo</span>
                                            </div>
                                            <p className="text-[#6f6b60] text-sm mb-4">{PLANS.pro.description}</p>
                                            <ul className="space-y-2">
                                                {PLANS.pro.features.map((feature, idx) => (
                                                    <li key={idx} className="flex items-center gap-2 text-sm">
                                                        <Check size={16} className="text-[#16a34a]"/>
                                                        <span className="text-[#3f3b32]">{feature.text}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </button>
                                    </div>
                                </>
                            ) : selectedPlan === 'pro' ? (
                                <>
                                    {/* Upgrade Confirmation */}
                                    <div className="text-center mb-6">
                                        <div className="bg-[#dcfce7] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Sparkles size={32} className="text-[#16a34a]"/>
                                        </div>
                                        <h4 className="text-xl font-bold text-[#0a0a0a] mb-2">Upgrade to Pro</h4>
                                        <p className="text-[#6f6b60]">Unlock unlimited rules with a 1-month free trial</p>
                                    </div>

                                    <div className="bg-[#f4f1e6] rounded-xl p-4 mb-6">
                                        <h5 className="font-bold text-[#0a0a0a] mb-3">What you'll get:</h5>
                                        <ul className="space-y-2">
                                            {PLANS.pro.features.map((feature, idx) => (
                                                <li key={idx} className="flex items-center gap-2 text-sm">
                                                    <Check size={16} className="text-[#16a34a]"/>
                                                    <span className="text-[#3f3b32]">{feature.text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="bg-[#e0f2fe] border border-[#bae6fd] rounded-xl p-4 mb-6">
                                        <p className="text-[#075985] text-sm">
                                            <span className="font-bold">Free for 30 days.</span> You won't be charged until {formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString())}.
                                            Cancel anytime before then.
                                        </p>
                                    </div>

                                    <div className="flex gap-3">
                                        <Button
                                            variant="secondary"
                                            onClick={() => setChangePlanStep('select')}
                                            className="flex-1"
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleConfirmUpgrade}
                                            disabled={checkoutLoading}
                                            className="flex-1"
                                        >
                                            {checkoutLoading ? 'Processing...' : (
                                                <>
                                                    Start Free Trial
                                                    <ArrowRight size={18}/>
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Cancel Confirmation */}
                                    <div className="mb-6">
                                        <h4 className="text-lg font-bold text-[#0a0a0a] mb-2">
                                            {isCanceling ? 'Cancellation already scheduled' : 'Cancel Pro subscription'}
                                        </h4>
                                        <p className="text-[#6f6b60] text-sm">
                                            {isCanceling
                                                ? `Your subscription is already set to cancel on ${formatDate(isTrialing ? subscriptionStatus?.trial_ends_at : subscriptionStatus?.current_period_end)}. You'll keep Pro until then, then move to Free — nothing more to do.`
                                                : isTrialing
                                                ? `You're on a free trial, so you won't be charged. You'll keep Pro until ${formatDate(subscriptionStatus?.trial_ends_at)}, then move to Free.`
                                                : `You'll keep Pro until ${formatDate(subscriptionStatus?.current_period_end)}, then your workspace moves to the Free plan. You won't be charged again. Cancelled within 14 days of your first payment and want a full refund? Email contact@reviewme.dev.`}
                                        </p>
                                    </div>

                                    {/* What will change */}
                                    <div className="bg-[#f4f1e6] rounded-xl p-4 mb-6">
                                        <h5 className="font-bold text-[#0a0a0a] mb-3">What will change:</h5>
                                        <ul className="space-y-2">
                                            <li className="flex items-center gap-2 text-sm">
                                                <X size={16} className="text-[#ef4444]"/>
                                                <span className="text-[#3f3b32]">Unlimited rules → 1 active rule</span>
                                            </li>
                                            <li className="flex items-center gap-2 text-sm">
                                                <X size={16} className="text-[#ef4444]"/>
                                                <span className="text-[#3f3b32]">Priority support → Standard support</span>
                                            </li>
                                        </ul>
                                    </div>

                                    {/* Rules that will be disabled */}
                                    {downgradePreview && downgradePreview.rules_to_disable.length > 0 && (
                                        <div className="mb-6 p-4 bg-[#fef3c7] border border-[#fde68a] rounded-xl">
                                            <p className="text-[#92400e] font-bold text-sm mb-2">
                                                These rules will be disabled:
                                            </p>
                                            <ul className="text-[#b45309] text-sm space-y-1">
                                                {downgradePreview.rules_to_disable.map(rule => (
                                                    <li key={rule.id} className="flex items-center gap-2">
                                                        <span className="w-1.5 h-1.5 bg-[#f59e0b] rounded-full" />
                                                        {rule.name}
                                                    </li>
                                                ))}
                                            </ul>
                                            <p className="text-[#b45309] text-xs mt-2">
                                                Your oldest active rule will remain enabled. Rules are disabled, not deleted.
                                            </p>
                                        </div>
                                    )}

                                    {/* Feedback */}
                                    <div className="mb-6">
                                        <h5 className="font-bold text-[#0a0a0a] mb-3">Help us improve (optional)</h5>
                                        <p className="text-[#6f6b60] text-sm mb-3">Why are you switching plans?</p>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {DOWNGRADE_REASONS.map(reason => (
                                                <button
                                                    key={reason.id}
                                                    onClick={() => toggleReason(reason.id)}
                                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                                        selectedReasons.includes(reason.id)
                                                            ? 'bg-[#dcfce7] text-[#15803d]'
                                                            : 'bg-[#ece8db] text-[#6f6b60] hover:bg-[#e4e0d2]'
                                                    }`}
                                                >
                                                    {reason.label}
                                                </button>
                                            ))}
                                        </div>
                                        {selectedReasons.includes('other') && (
                                            <textarea
                                                value={otherReason}
                                                onChange={(e) => setOtherReason(e.target.value)}
                                                placeholder="Tell us more..."
                                                className="w-full px-3 py-2 border border-[#d3cdba] bg-white rounded-lg text-sm resize-none h-20 placeholder:text-[#a8a394] focus:border-[#22c55e] focus:ring-2 focus:ring-[#22c55e]/30 outline-none"
                                            />
                                        )}
                                    </div>

                                    <div className="flex gap-3">
                                        <Button
                                            variant="secondary"
                                            onClick={() => setChangePlanStep('select')}
                                            className="flex-1"
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={handleConfirmCancel}
                                            disabled={downgradeLoading || isCanceling}
                                            className="flex-1"
                                        >
                                            {isCanceling ? 'Already scheduled' : downgradeLoading ? 'Processing...' : 'Confirm Cancellation'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Billing;
